/** @jsxImportSource theme-ui */
import React from 'react';

import { AggregationCat } from 'models/DiseaseModels';
import theme from 'theme';
import { valignHackBottom, valignHackTop } from 'utils/cssHelpers';
import { MapAggregationCat2LegendLabel } from 'utils/modelHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import { FlexGroup } from 'components/_common/FlexGroup';
import { Typography } from 'components/_common/Typography';
import { TypographyColor } from 'components/_common/Typography/Typography';

type DaGraphLegendProps = {
  aggregationCategory: AggregationCat;
  showAvg?: boolean;
};

export const DaGraphLegend: React.FC<DaGraphLegendProps> = ({
  aggregationCategory,
  showAvg = false,
}) => {
  return (
    <div
      sx={{
        display: 'flex',
        justifyContent: 'left',
        '& .legend-item:not(:first-child)': {
          ml: '8px',
        },
      }}
    >
      <DaGraphLegendGroup text="Cases" gutter="0">
        <DaGraphLegendGraphic color="sea30" />
      </DaGraphLegendGroup>
      <DaGraphLegendGroup text="Deaths" gutter="0">
        <DaGraphLegendGraphic color="clay80" />
      </DaGraphLegendGroup>
      <DaGraphLegendGroup text="Timeframe" gutter="0">
        <DaGraphLegendGraphic color="sea50" />
      </DaGraphLegendGroup>
      {showAvg && (
        <DaGraphLegendGroup text={MapAggregationCat2LegendLabel[aggregationCategory]} gutter="4px">
          <DaGraphLegendLine color={theme.colors.sea90} />
        </DaGraphLegendGroup>
      )}
    </div>
  );
};

// =====================================================================================================================================

export const DaGraphWrtAoiLegend: React.FC<DaGraphLegendProps> = ({
  aggregationCategory,
  showAvg = false,
}) => {
  return (
    <div
      sx={{
        display: 'flex',
        justifyContent: 'left',
        '& .legend-item:not(:first-child)': {
          ml: '8px',
        },
      }}
    >
      <DaGraphLegendGroup text="Timeframe" gutter="0">
        <DaGraphLegendGraphic color="sea50" />
      </DaGraphLegendGroup>
      {showAvg && (
        <DaGraphLegendGroup text={MapAggregationCat2LegendLabel[aggregationCategory]} gutter="4px">
          <DaGraphLegendLine color={theme.colors.sea90} />
        </DaGraphLegendGroup>
      )}
    </div>
  );
};

// =====================================================================================================================================

type AoiVsNestingAoiLegendProps = { aoiName: string; aoiCountry: string; isCountry: boolean };
export const AoiVsNestingAoiLegend: React.FC<AoiVsNestingAoiLegendProps> = ({
  aoiName,
  aoiCountry,
  isCountry,
}) => (
  <div>
    <Typography inline variant="caption" color="stone70">
      Cases in
    </Typography>
    <BdIcon name="icon-graph" color="seafoam100" sx={{ ...valignHackTop('1px') }} />
    <Typography inline variant="caption2" color="seafoam100">
      {aoiName}
    </Typography>
    {!isCountry && (
      <>
        <Typography inline variant="caption" color="stone70">
          {' '}
          vs.
        </Typography>
        <BdIcon name="icon-graph" color="sea30" sx={{ ...valignHackTop('1px') }} />
        <Typography inline variant="caption" color="stone70">
          {aoiCountry}
        </Typography>
      </>
    )}
  </div>
);

// =====================================================================================================================================

const DaGraphLegendGroup: React.FC<{ text: string; gutter: string }> = ({
  text,
  gutter,
  children,
}) => (
  <FlexGroup prefix={children} gutter={gutter} className="legend-item" noMinWidth>
    <Typography variant="caption" color="stone70" inline nowrap>
      {text}
    </Typography>
  </FlexGroup>
);

const DaGraphLegendGraphic: React.FC<{ color: TypographyColor }> = ({ color }) => (
  <BdIcon name="icon-graph" color={color} sx={{ ...valignHackTop('1px') }} nomargin />
);

const DaGraphLegendLine: React.FC<{ color: string }> = ({ color }) => (
  <div
    sx={{
      width: '8px',
      height: '2px',
      background: color,
      ...valignHackBottom('4px'),
    }}
  />
);
